import { useEffect, useState } from "react";
import { api, formatApiErrorDetail, fmtDate } from "@/lib/api";
import { PageHeader, Section, Pill } from "@/components/ui-kit";
import { toast } from "sonner";
import { Plus, Copy, Pencil, Trash2 } from "lucide-react";

const CURRENCIES = [
  ["USD", "US Dollar"], ["NGN", "Nigerian Naira"], ["EUR", "Euro"],
  ["GBP", "British Pound"], ["GHS", "Ghanaian Cedi"], ["KES", "Kenyan Shilling"], ["ZAR", "South African Rand"],
];

export default function CompaniesPage() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", admin_email: "", admin_name: "", admin_password: "", primary_color: "#2C5545", currency: "USD" });
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/companies").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const submitCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/companies", form);
      toast.success("Tenant created.");
      setOpen(false);
      setForm({ name: "", admin_email: "", admin_name: "", admin_password: "", primary_color: "#2C5545", currency: "USD" });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/admin/companies/${editing.id}`, {
        name: editing.name, primary_color: editing.primary_color,
        currency: editing.currency, subscription_status: editing.subscription_status,
      });
      toast.success("Tenant updated.");
      setEditing(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const remove = async (c) => {
    const mode = window.prompt(
      `Delete tenant "${c.name}"?\nChoose deletion mode:\n\n` +
      `  block   — only if it has no users/data (safe)\n` +
      `  soft    — mark INACTIVE, keep all records\n` +
      `  cascade — DESTRUCTIVE: purge users, customers, savings, loans, journals\n\n` +
      `Type one of: block | soft | cascade`,
      "block"
    );
    if (!mode) return;
    if (!["block", "soft", "cascade"].includes(mode)) return toast.error("Invalid mode.");
    if (mode === "cascade" && !window.confirm(`This will permanently delete EVERYTHING for ${c.name}. Continue?`)) return;
    try {
      const { data } = await api.delete(`/admin/companies/${c.id}?mode=${mode}`);
      toast.success(`Tenant ${data.mode === "soft" ? "deactivated" : "deleted"}.`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const copy = (txt) => { navigator.clipboard.writeText(txt); toast.success("Copied"); };
  const inputCls = "w-full bg-white border border-border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-sm";

  return (
    <div data-testid="companies-page">
      <PageHeader title="Tenants" subtitle="Global Admin · Companies"
        actions={<button onClick={() => { setOpen(true); setEditing(null); }} className="btn-primary" data-testid="new-tenant-btn"><Plus className="w-4 h-4" /> New Tenant</button>}
      />

      {open && (
        <Section title="Onboard new tenant" testId="new-tenant-form" actions={<button onClick={() => setOpen(false)} className="btn-ghost">Cancel</button>}>
          <form onSubmit={submitCreate} className="grid md:grid-cols-2 gap-4">
            <div><label className="label block mb-1">Company Name</label><input required className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="tenant-name" /></div>
            <div><label className="label block mb-1">Currency</label>
              <select required className={inputCls} value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} data-testid="tenant-currency">
                {CURRENCIES.map(([c, n]) => <option key={c} value={c}>{c} — {n}</option>)}
              </select>
            </div>
            <div><label className="label block mb-1">Admin Name</label><input required className={inputCls} value={form.admin_name} onChange={(e) => setForm({ ...form, admin_name: e.target.value })} data-testid="tenant-admin-name" /></div>
            <div><label className="label block mb-1">Admin Email</label><input required type="email" className={inputCls} value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })} data-testid="tenant-admin-email" /></div>
            <div><label className="label block mb-1">Admin Password</label><input required minLength={8} type="text" className={inputCls} value={form.admin_password} onChange={(e) => setForm({ ...form, admin_password: e.target.value })} data-testid="tenant-admin-password" /></div>
            <div><label className="label block mb-1">Primary Color</label><input className={inputCls} type="color" value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} data-testid="tenant-primary-color" /></div>
            <div className="md:col-span-2 flex justify-end"><button type="submit" className="btn-primary" data-testid="tenant-submit-btn">Create tenant</button></div>
          </form>
        </Section>
      )}

      {editing && (
        <Section title={`Edit · ${editing.name}`} testId="edit-tenant-form" actions={<button onClick={() => setEditing(null)} className="btn-ghost">Cancel</button>}>
          <form onSubmit={submitEdit} className="grid md:grid-cols-2 gap-4">
            <div><label className="label block mb-1">Company Name</label><input required className={inputCls} value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="edit-tenant-name" /></div>
            <div><label className="label block mb-1">Currency</label>
              <select className={inputCls} value={editing.currency} onChange={(e) => setEditing({ ...editing, currency: e.target.value })} data-testid="edit-tenant-currency">
                {CURRENCIES.map(([c, n]) => <option key={c} value={c}>{c} — {n}</option>)}
              </select>
            </div>
            <div><label className="label block mb-1">Subscription Status</label>
              <select className={inputCls} value={editing.subscription_status} onChange={(e) => setEditing({ ...editing, subscription_status: e.target.value })} data-testid="edit-tenant-status">
                {["TRIAL", "ACTIVE", "PAST_DUE", "CANCELED", "INACTIVE"].map((s) => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div><label className="label block mb-1">Primary Color</label><input className={inputCls} type="color" value={editing.primary_color || "#2C5545"} onChange={(e) => setEditing({ ...editing, primary_color: e.target.value })} data-testid="edit-tenant-color" /></div>
            <div className="md:col-span-2 flex justify-end"><button type="submit" className="btn-primary" data-testid="edit-tenant-submit-btn">Save changes</button></div>
          </form>
        </Section>
      )}

      <Section title="All tenants" testId="tenants-list">
        {items.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center" data-testid="empty-tenants">No tenants yet. Click "New Tenant" to onboard one.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left">
                <th className="label py-2">Name</th>
                <th className="label py-2">ID</th>
                <th className="label py-2">Subscription</th>
                <th className="label py-2 text-right">Users</th>
                <th className="label py-2 text-right">Customers</th>
                <th className="label py-2">Created</th>
                <th className="label py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id} className="border-t border-border">
                  <td className="py-2.5 font-medium">{c.name}</td>
                  <td className="py-2.5 font-mono text-xs">
                    {c.id.slice(0, 8)}…
                    <button onClick={() => copy(c.id)} className="ml-2 text-muted-foreground hover:text-foreground" data-testid={`copy-${c.id}`}><Copy className="w-3 h-3 inline" /></button>
                  </td>
                  <td className="py-2.5"><Pill variant={c.subscription_status === "ACTIVE" ? "success" : c.subscription_status === "TRIAL" ? "info" : "warning"}>{c.subscription_status}</Pill></td>
                  <td className="py-2.5 text-right tabular-nums">{c.user_count}</td>
                  <td className="py-2.5 text-right tabular-nums">{c.customer_count}</td>
                  <td className="py-2.5">{fmtDate(c.created_at)}</td>
                  <td className="py-2.5 text-right">
                    <div className="inline-flex gap-1">
                      <button className="btn-ghost px-2 py-1 text-xs" onClick={() => { setEditing({ ...c }); setOpen(false); }} data-testid={`edit-tenant-${c.id}`}>
                        <Pencil className="w-3 h-3" /> Edit
                      </button>
                      <button className="btn-ghost px-2 py-1 text-xs text-secondary" onClick={() => remove(c)} data-testid={`delete-tenant-${c.id}`}>
                        <Trash2 className="w-3 h-3" /> Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>
    </div>
  );
}
